import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { PasswordInput } from '../components/PasswordInput';
import AdminCredits from '../components/AdminCredits';
import { User, UserRole } from '../types';

const Admin: React.FC = () => {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user' as UserRole });
  const [creating, setCreating] = useState(false);

  // A1 — inline edit + set-password row state
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ username: '', email: '' });
  const [pwId, setPwId] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState('');

  // A2 — soft-delete
  const [showDeleted, setShowDeleted] = useState(false);

  // A3 — search / filter / pagination
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');               // debounced search term actually queried
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  // Debounce the search box → q.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const apiError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return fallback;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authService.adminListUsers({
        q: q || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        includeDeleted: showDeleted,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setUsers(res.users);
      setTotal(res.total);
    } catch (err: any) {
      setError(apiError(err, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [showDeleted, q, roleFilter, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const removeUser = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await authService.adminDeleteUser(id);
      await load();
    } catch (err: any) {
      setError(apiError(err, 'Delete failed.'));
    } finally {
      setBusyId(null);
    }
  };

  const restoreUser = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await authService.adminRestoreUser(id);
      await load();
    } catch (err: any) {
      setError(apiError(err, 'Restore failed.'));
    } finally {
      setBusyId(null);
    }
  };

  const patch = async (id: string, data: { is_active?: boolean; role?: UserRole }) => {
    setBusyId(id);
    setError(null);
    try {
      const updated = await authService.adminUpdateUser(id, data);
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
    } catch (err: any) {
      setError(apiError(err, 'Update failed.'));
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (u: User) => {
    setPwId(null);
    setEditId(u.id);
    setDraft({ username: u.username, email: u.email });
  };

  const saveEdit = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      const updated = await authService.adminUpdateUser(id, {
        username: draft.username.trim(),
        email: draft.email.trim(),
      });
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
      setEditId(null);
    } catch (err: any) {
      setError(apiError(err, 'Edit failed.'));
    } finally {
      setBusyId(null);
    }
  };

  const submitPassword = async (id: string) => {
    if (pwValue.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await authService.adminSetPassword(id, pwValue);
      setPwId(null);
      setPwValue('');
    } catch (err: any) {
      setError(apiError(err, 'Could not set password.'));
    } finally {
      setBusyId(null);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const created = await authService.adminCreateUser({
        username: form.username.trim(), email: form.email.trim(), password: form.password, role: form.role,
      });
      setUsers((prev) => [created, ...prev]);
      setForm({ username: '', email: '', password: '', role: 'user' });
      setShowCreate(false);
    } catch (err: any) {
      setError(apiError(err, 'Could not create user.'));
    } finally {
      setCreating(false);
    }
  };

  const fmt = (iso?: string | null, tz?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const date = isNaN(d.getTime()) ? iso : d.toLocaleString();
    return tz ? `${date} (${tz})` : date;
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.04em' }}>Users</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-faint)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {total} total
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-faint)', fontSize: 12 }}>
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
          <button className="sidebar-new-run-btn" onClick={() => setShowCreate((s) => !s)}>
            {showCreate ? 'Cancel' : '+ Add user'}
          </button>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input placeholder="Search username or email…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, minWidth: 220 }} />
        <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }} style={{ ...inputStyle, flex: '0 0 auto' }}>
          <option value="">All roles</option>
          <option value="admin">admin</option>
          <option value="user">user</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} style={{ ...inputStyle, flex: '0 0 auto' }}>
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
      </div>

      {showCreate && (
        <form onSubmit={create} style={createFormStyle}>
          <input placeholder="Username" value={form.username} required minLength={3}
            onChange={(e) => setForm({ ...form, username: e.target.value })} style={inputStyle} />
          <input placeholder="Email" type="email" value={form.email} required
            onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
          <PasswordInput placeholder="Password (8+)" value={form.password} required minLength={8}
            onChange={(v) => setForm({ ...form, password: v })} style={inputStyle}
            wrapperStyle={{ flex: '1 1 150px', minWidth: 150 }} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })} style={inputStyle}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button type="submit" disabled={creating} className="sidebar-new-run-btn">
            {creating ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-faint)', padding: 20 }}>Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['User', 'Email', 'Role', 'Status', 'Verified', 'Runs', 'Last login', 'Actions'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                const editing = editId === u.id;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      {editing ? (
                        <input value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                          style={{ ...inputStyle, minWidth: 120 }} />
                      ) : (
                        <>{u.username}{isSelf && <span style={selfTagStyle}>you</span>}</>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editing ? (
                        <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                          style={{ ...inputStyle, minWidth: 160 }} />
                      ) : u.email}
                    </td>
                    <td style={tdStyle}><span style={u.role === 'admin' ? roleAdminStyle : roleUserStyle}>{u.role}</span></td>
                    <td style={tdStyle}>
                      <span style={{ color: u.is_active ? 'var(--category-good-fg)' : 'var(--text-faint)' }}>
                        {u.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>{u.email_verified ? '✓' : '—'}</td>
                    <td style={tdStyle}>
                      {(u.run_count ?? 0) > 0 ? (
                        <button
                          onClick={() => navigate(`/history?user=${u.id}&name=${encodeURIComponent(u.username)}`)}
                          title="View this user's runs"
                          style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', fontSize: 13, padding: 0, fontWeight: 600 }}
                        >
                          {u.run_count}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--text-faint)' }}>0</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{fmt(u.last_login, u.last_login_tz)}</td>
                    <td style={tdStyle}>
                      {editing ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button disabled={busyId === u.id} onClick={() => saveEdit(u.id)} style={actionBtnStyle}>Save</button>
                          <button onClick={() => setEditId(null)} style={actionBtnStyle}>Cancel</button>
                        </div>
                      ) : pwId === u.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <PasswordInput placeholder="New password (8+)" value={pwValue} autoFocus
                            onChange={setPwValue} style={inputStyle}
                            wrapperStyle={{ minWidth: 150 }} />
                          <button disabled={busyId === u.id} onClick={() => submitPassword(u.id)} style={actionBtnStyle}>Set</button>
                          <button onClick={() => { setPwId(null); setPwValue(''); }} style={actionBtnStyle}>Cancel</button>
                        </div>
                      ) : (
                        u.deleted_at ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>deleted</span>
                            <button disabled={busyId === u.id} onClick={() => restoreUser(u.id)} style={actionBtnStyle}>Restore</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button disabled={busyId === u.id} onClick={() => patch(u.id, { is_active: !u.is_active })} style={actionBtnStyle}>
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button disabled={busyId === u.id} onClick={() => patch(u.id, { role: u.role === 'admin' ? 'user' : 'admin' })} style={actionBtnStyle}>
                              {u.role === 'admin' ? 'Make user' : 'Make admin'}
                            </button>
                            <button disabled={busyId === u.id} onClick={() => startEdit(u)} style={actionBtnStyle}>Edit</button>
                            <button disabled={busyId === u.id} onClick={() => { setEditId(null); setPwId(u.id); setPwValue(''); }} style={actionBtnStyle}>Password</button>
                            {u.id !== me?.id && (
                              <button disabled={busyId === u.id}
                                onClick={() => { if (window.confirm(`Delete ${u.username}? They can be restored later.`)) removeUser(u.id); }}
                                style={{ ...actionBtnStyle, color: 'var(--category-dead-fg)', borderColor: 'var(--category-dead-fg)' }}>
                                Delete
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 12, fontSize: 12, color: 'var(--text-faint)' }}>
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={actionBtnStyle}>Prev</button>
          <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} style={actionBtnStyle}>Next</button>
        </div>
      )}

      {/* CCK credits: per-user breakdown + manual grant + coupon-code management (T4). */}
      <AdminCredits />
    </div>
  );
};

const errorStyle: React.CSSProperties = {
  marginBottom: 12, padding: '10px 12px', border: '1px solid var(--category-dead-fg)',
  background: 'var(--category-dead-bg)', color: 'var(--category-dead-fg)', fontSize: 13,
};
const createFormStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, padding: 12,
  border: '1px solid var(--border)', background: 'var(--surface)',
};
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', background: 'var(--surface-subtle)', color: 'var(--text)',
  border: '1px solid var(--border)', outline: 'none', minWidth: 150, flex: '1 1 150px',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', background: 'var(--surface)', color: 'var(--text-soft)',
  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = { padding: '8px 12px', color: 'var(--text)' };
const actionBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)',
  padding: '4px 8px', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap',
};
const roleAdminStyle: React.CSSProperties = { color: 'var(--amber)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em' };
const roleUserStyle: React.CSSProperties = { color: 'var(--text-soft)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em' };
const selfTagStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: 9, color: 'var(--text-faint)', border: '1px solid var(--border)',
  padding: '1px 4px', textTransform: 'uppercase', letterSpacing: '0.08em',
};

export default Admin;
